/**
 * First-party site analytics client.
 *
 * Sends consent-gated behaviour/funnel events to our own backend
 * (`POST /api/analytics/ingest/`) — complementing Microsoft Clarity / GTM,
 * not replacing them. Events are buffered and flushed in batches; the queue
 * is also flushed on page hide via `navigator.sendBeacon` so drop-off /
 * last-page events survive navigation (important on mobile).
 *
 * Privacy:
 * - Only fires with analytics permission in the granular consent record.
 *   Consent is checked again before transport. No-ops for bots/prerender.
 * - Sends only an event name, route path, and a small non-PII metadata object,
 *   plus the first-party `guestSessionId` (the server stores only its hash).
 *
 * Transport: text/plain body (a CORS "simple request" — no preflight, works
 * same-origin in prod and cross-origin in dev, and via sendBeacon on unload).
 */
import { readConsent } from "./consent";

const API_URL = process.env.REACT_APP_API_URL || "";
export const INGEST_URL = `${API_URL.replace(/\/+$/, "")}/analytics/ingest/`;
const GUEST_KEY = "guestSessionId";
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH = 20;

// Canonical event names — must match api/analytics/events.py CLIENT_EVENTS.
export const EVENTS = Object.freeze({
  PAGE_VIEW: "page_view",
  ROUTE_CHANGE: "route_change",
  APP_LOADED: "app_loaded",
  CHAT_OPENED: "chat_opened",
  CHAT_ABANDONED: "chat_abandoned",
  WELCOME_SCREEN_VIEWED: "welcome_screen_viewed",
  SUGGESTED_PROMPT_CLICKED: "suggested_prompt_clicked",
  PHOTO_SELECTED: "photo_selected",
  PHOTO_UPLOADED: "photo_uploaded",
  GUEST_SIGNUP_PROMPT_SHOWN: "guest_signup_prompt_shown",
  GUEST_SIGNUP_CTA_CLICKED: "guest_signup_cta_clicked",
  BLOG_READ: "blog_read",
  COST_GUIDE_VIEWED: "cost_guide_viewed",
  COST_LANDING_VIEWED: "cost_landing_viewed",
  CTA_CLICKED: "cta_clicked",
  SIGNUP_PATH_SELECTED: "signup_path_selected",
  SIGNUP_STARTED: "signup_started",
  OUTBOUND_CLICK: "outbound_click",
  DROP_OFF: "drop_off",
  CONTRACTOR_SELECTED: "contractor_selected",
  // "Did you find this page helpful?" on the cost guides. Must stay in step
  // with CLIENT_EVENTS in api/analytics/events.py or the endpoint drops it.
  PAGE_FEEDBACK: "page_feedback",
});

const BOT_RE =
  /bot|crawl|spider|slurp|reactsnap|headless|lighthouse|pingdom|prerender|gtmetrix|phantom|puppeteer|playwright|facebookexternalhit|chrome-lighthouse/i;

let queue = [];
let flushTimer = null;
let unloadBound = false;

function isBotEnv() {
  if (typeof navigator === "undefined") return true;
  return BOT_RE.test(navigator.userAgent || "");
}

function hasConsent() { return readConsent().analytics; }

export function clearPendingEvents() {
  queue = [];
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
}

function getGuestSessionId() {
  try {
    return localStorage.getItem(GUEST_KEY) || null;
  } catch {
    return null;
  }
}

function enabled() {
  return typeof window !== "undefined" && !isBotEnv() && hasConsent();
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

function bindUnloadFlush() {
  if (unloadBound || typeof window === "undefined") return;
  unloadBound = true;
  window.addEventListener("pagehide", () => flush(true));
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush(true);
    });
  }
}

/**
 * Queue an analytics event. Safe to call anywhere — no-ops without consent,
 * for bots, or on any error (analytics must never break the app).
 */
export function queueEvent(eventType, metadata = {}, path = null) {
  try {
    if (!enabled()) return;
    queue.push({
      event_type: eventType,
      path: path || (typeof window !== "undefined" ? window.location.pathname : null),
      metadata: metadata && typeof metadata === "object" ? metadata : {},
    });
    bindUnloadFlush();
    if (queue.length >= MAX_BATCH) {
      flush();
    } else {
      scheduleFlush();
    }
  } catch {
    /* never break the app */
  }
}

/** Convenience: record a page/route view. */
export function trackPageView(path, metadata = {}) {
  queueEvent(EVENTS.PAGE_VIEW, metadata, path);
}

/**
 * Record a consent choice server-side. Deliberately BYPASSES the consent gate:
 * UK GDPR accountability means we must be able to demonstrate refusals as well
 * as grants. Carries no behavioural data — just the choice, method and policy
 * version. Still no-ops for bots and never throws.
 */
export function recordConsentChoice(choices) {
  try {
    if (typeof window === "undefined" || isBotEnv()) return;
    const body = JSON.stringify({
      events: [
        {
          event_type: "consent_updated",
          path: window.location.pathname,
          metadata: choices && typeof choices === "object" ? choices : {},
        },
      ],
      guest_session_id: getGuestSessionId(),
    });
    fetch(INGEST_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never break the app */
  }
}

/**
 * Flush the queue to the backend. Uses sendBeacon when `useBeacon` is true
 * (page-hide path) so events survive unload; otherwise a keepalive fetch.
 */
export function flush(useBeacon = false) {
  try {
    if (!enabled()) { clearPendingEvents(); return; }
    if (!queue.length) return;
    const body = JSON.stringify({
      events: queue,
      guest_session_id: getGuestSessionId(),
      consent: { analytics: true },
    });
    queue = [];

    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
      navigator.sendBeacon(INGEST_URL, blob);
      return;
    }

    fetch(INGEST_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}
